/**
 * DiscoveryScreen v2 — Phantom-style 2×2 Featured launcher + compact list
 * Drop-in replacement for apps/mobile/src/screens/DiscoveryScreen.tsx
 *
 * Changes vs v1:
 * - First 4 filtered dApps shown as large 2×2 Featured tiles (like Phantom)
 * - Remaining dApps shown as compact horizontal list rows
 * - Category pills with glow on active
 * - dApp open button styled as brand ghost
 * ALL imports, store hooks, FEATURED_DAPPS data — UNCHANGED.
 */

import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { FEATURED_DAPPS } from '@properwallet/chain-eos'
import { useAppStore } from '../store/app.store'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'
import BottomNav from '../components/BottomNav'
import DAppWebView from '../components/DAppWebView'
import AccountAvatar from '../components/AccountAvatar'

type Category = 'all' | 'defi' | 'nft' | 'tools' | 'games' | 'favorites'

export default function DiscoveryScreen() {
  const { t } = useTranslation()
  const { favoriteDApps, toggleFavoriteDApp, discoveryUrl, setDiscoveryUrl } = useAppStore()
  const { activeAccount } = useWalletStore()
  const [activeCategory, setActiveCategory] = useState<Category>('all')
  const [openUrl, setOpenUrl] = useState<string | null>(null)
  const [customUrl, setCustomUrl] = useState('')
  const [showUrlInput, setShowUrlInput] = useState(false)

  const categories: Array<{ id: Category; label: string }> = [
    { id: 'all',       label: t('discovery.all') },
    { id: 'defi',      label: t('discovery.defi') },
    { id: 'nft',       label: t('discovery.nft') },
    { id: 'tools',     label: t('discovery.tools') },
    { id: 'games',     label: t('discovery.games') },
    { id: 'favorites', label: t('discovery.favorites') },
  ]

  const filtered = FEATURED_DAPPS.filter(app => {
    if (activeCategory === 'favorites') return favoriteDApps.includes(app.id)
    if (activeCategory === 'all') return true
    return app.category === activeCategory
  })

  // Featured grid: apps with featured: true (within current category filter)
  const featured = filtered.filter(app => app.featured)
  const rest     = filtered.filter(app => !app.featured)

  // Opened from an account chip ("view in explorer") — consume the request once.
  useEffect(() => {
    if (!discoveryUrl) return
    setOpenUrl(discoveryUrl)
    setDiscoveryUrl(null)
  }, [discoveryUrl, setDiscoveryUrl])

  const handleOpenUrl = () => {
    const url = customUrl.startsWith('http') ? customUrl : `https://${customUrl}`
    setOpenUrl(url)
    setShowUrlInput(false)
    setCustomUrl('')
  }

  // 'tools' entries (block explorers, name-bid boards, help centers, etc.) are
  // read-only — they never need the wallet-bridge injection DAppWebView gives
  // real dApps. On native, DAppWebView still opens them fine (a separate
  // system WebView, no iframe involved). On web, DAppWebView's fallback is a
  // literal <iframe>, and these sites' own X-Frame-Options/CSP frame-ancestors
  // headers legitimately refuse to be framed (anti-clickjacking) — so on web
  // we open a plain new tab instead of hitting that wall.
  const openAppUrl = (app: { url: string; category: string }) => {
    if (app.category === 'tools' && !Capacitor.isNativePlatform()) {
      window.open(app.url, '_blank', 'noopener,noreferrer')
    } else {
      setOpenUrl(app.url)
    }
  }

  if (openUrl) {
    return <DAppWebView url={openUrl} onClose={() => setOpenUrl(null)} />
  }

  return (
    <div className="flex flex-col h-full" style={{ background: '#000000' }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div
        className="pt-safe px-4 pb-4 flex-shrink-0"
        style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <AccountAvatar avatarUrl={activeAccount?.avatarUrl} size="xs" />
            <h2 className="text-white font-bold" style={{ fontSize: 20 }}>
              {t('discovery.title')}
            </h2>
          </div>

          {/* URL button */}
          <button
            onClick={() => setShowUrlInput(v => !v)}
            className="btn-ghost flex items-center gap-1.5"
            style={{ padding: '6px 12px', fontSize: 12, height: 34 }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            URL
          </button>
        </div>

        {/* Custom URL input */}
        {showUrlInput && (
          <div className="flex gap-2 mb-3 animate-fade-in">
            <input
              type="url"
              value={customUrl}
              onChange={e => setCustomUrl(e.target.value)}
              placeholder={t('discovery.urlPlaceholder')}
              className="input-field flex-1"
              style={{ height: 40, fontSize: 13 }}
              onKeyDown={e => e.key === 'Enter' && handleOpenUrl()}
              autoFocus
            />
            <button
              onClick={handleOpenUrl}
              className="btn-primary"
              style={{ padding: '0 18px', height: 40, fontSize: 13 }}
            >
              →
            </button>
          </div>
        )}

        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`tab-pill flex-shrink-0${activeCategory === cat.id ? ' active' : ''}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────── */}
      <div className="scroll-area flex-1 px-4 py-5">

        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <p className="text-sm text-center" style={{ color: '#6b7280' }}>
              {activeCategory === 'favorites' ? t('discovery.noFavorites') : t('common.loading')}
            </p>
          </div>
        ) : (
          <>
            {/* ── Featured 2×2 Phantom-style launcher ─────────── */}
            {featured.length > 0 && (
              <>
                <p className="section-label">Featured</p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {featured.map(app => {
                    const isFav = favoriteDApps.includes(app.id)
                    return (
                      <div
                        key={app.id}
                        className="relative flex flex-col items-center text-center"
                        style={{
                          background: '#0c100c',
                          border: '1px solid rgba(255,255,255,0.06)',
                          borderRadius: 20,
                          padding: '18px 14px 14px',
                          cursor: 'pointer',
                        }}
                      >
                        {/* Fav star */}
                        <button
                          onClick={() => toggleFavoriteDApp(app.id)}
                          style={{
                            position: 'absolute', top: 10, right: 10,
                            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                          }}
                          title={isFav ? t('discovery.removeFavorite') : t('discovery.addFavorite')}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24"
                            fill={isFav ? '#f59e0b' : 'none'}
                            stroke={isFav ? '#f59e0b' : '#4b5563'}
                            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                          </svg>
                        </button>

                        {/* Large app icon */}
                        <div
                          className="flex items-center justify-center mb-3 overflow-hidden"
                          style={{
                            width: 60, height: 60, borderRadius: 18,
                            background: '#141914',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                          }}
                        >
                          <img
                            src={app.logo}
                            alt={app.name}
                            style={{ width: 40, height: 40, objectFit: 'contain' }}
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                        </div>

                        <p className="text-white font-semibold leading-tight mb-1" style={{ fontSize: 13 }}>
                          {app.name}
                        </p>
                        <p className="line-clamp-2 mb-3" style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4 }}>
                          {app.description}
                        </p>

                        <button
                          onClick={() => openAppUrl(app)}
                          className="dapp-open-btn"
                        >
                          {t('discovery.openApp')} →
                        </button>
                      </div>
                    )
                  })}
                </div>
              </>
            )}

            {/* ── Rest: compact list ───────────────────────────── */}
            {rest.length > 0 && (
              <>
                <p className="section-label">All Apps</p>
                <div className="space-y-2">
                  {rest.map(app => {
                    const isFav = favoriteDApps.includes(app.id)
                    return (
                      <div
                        key={app.id}
                        className="flex items-center gap-3"
                        style={{
                          background: '#0c100c',
                          border: '1px solid rgba(255,255,255,0.05)',
                          borderRadius: 14,
                          padding: '11px 14px',
                        }}
                      >
                        {/* Icon */}
                        <div
                          className="flex-shrink-0 flex items-center justify-center overflow-hidden"
                          style={{ width: 44, height: 44, borderRadius: 13, background: '#141914' }}
                        >
                          <img
                            src={app.logo}
                            alt={app.name}
                            style={{ width: 30, height: 30, objectFit: 'contain' }}
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                          />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold" style={{ fontSize: 13 }}>{app.name}</p>
                          <p className="truncate mt-0.5" style={{ fontSize: 11, color: '#6b7280' }}>{app.description}</p>
                        </div>

                        {/* Fav */}
                        <button
                          onClick={() => toggleFavoriteDApp(app.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24"
                            fill={isFav ? '#f59e0b' : 'none'}
                            stroke={isFav ? '#f59e0b' : '#4b5563'}
                            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
                          </svg>
                        </button>

                        {/* Open */}
                        <button
                          onClick={() => openAppUrl(app)}
                          className="btn-ghost flex-shrink-0"
                          style={{ padding: '5px 12px', fontSize: 11, height: 32 }}
                        >
                          Open →
                        </button>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}

        <div style={{ height: 16 }} />
      </div>

      <BottomNav />
    </div>
  )
}
